import React from 'react';
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { getFixtureDisplay } from "@/utils/formatters.js";
import TeamDisplay from "@/components/TeamDisplay.jsx";
import { FIXTURE_STATE_CONFIG } from "@/utils/constants.js";
import i18n from "i18next";
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";

export const DemoMatchCard = ({ fixture, matchScore, isGoalFlashing, onTeamClick }) => {
    const { t } = useTranslation();
    const { brand } = useBrandConfig();

    if (!fixture || !fixture?.type) return;

    const stateDisplay = getFixtureDisplay(fixture.type, fixture.state, fixture.minutes, fixture.extraMinutes, t);

    const formatMatchDate = (dateString) => {
        if (!dateString) return '';
        const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
        const date = new Date(utcDateString);
        return new Intl.DateTimeFormat(i18n.language, {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
        }).format(date).replace(',', ' •').replace('.', '');
    };

    return (
        <div className="bg-white/70 backdrop-blur-2xl rounded-[2.5rem] p-6 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.15)] border border-white/60 -mt-10 relative z-20 transition-all font-body">

            {/* HEADER MATCH */}
            <div className="flex justify-between items-center border-b border-black/10 pb-4 mb-5">
                <div className="flex items-center gap-2">
                    {stateDisplay.isLive && (
                        <span className="w-2.5 h-2.5 rounded-full bg-score-neon animate-pulse shadow-[0_0_8px_#FF003C]" />
                    )}
                    <span className={`text-[11px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${stateDisplay.isLive ? 'text-score-neon' : 'bg-black/10 text-black/60'}`}>
                        {stateDisplay.label}
                    </span>
                    {fixture.state === FIXTURE_STATE_CONFIG.NOT_STARTED && fixture.startingAt && (
                        <>
                            <span className="text-black/20 text-[10px] mx-1">|</span>
                            <span className="text-[10px] font-black text-black/60 uppercase tracking-widest">
                                {formatMatchDate(fixture.startingAt)}
                            </span>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-1.5 bg-white/50 px-2.5 py-1 rounded-full border border-white/60 shadow-sm">
                    {fixture.league?.logoUrl && (
                        <img src={fixture.league.logoUrl} className="w-4 h-4 object-contain" alt="League" />
                    )}
                    <span className="text-[9px] font-black text-black/60 uppercase tracking-widest max-w-[80px] truncate">
                        {fixture.label}
                    </span>
                </div>
            </div>

            {/* TEAMS & SCORE */}
            <div className="flex justify-between items-center">
                <div className="w-[30%] flex justify-center">
                    <TeamDisplay team={fixture.homeTeam} onClick={onTeamClick ? () => onTeamClick('HOME') : undefined} />
                </div>

                <div className="flex flex-col items-center justify-center w-[40%] relative">
                    {fixture.state === FIXTURE_STATE_CONFIG.NOT_STARTED ? (
                        <div className="bg-black px-4 py-1.5 rounded-full shadow-lg transform -rotate-2">
                            <span className="text-xl font-heading italic tracking-widest leading-none" style={{ color: brand.colors.accent }}>VS</span>
                        </div>
                    ) : (
                        <motion.div
                            animate={isGoalFlashing ? {
                                scale: [1, 1.2, 1],
                                filter: [
                                    "drop-shadow(0px 1px 2px rgba(0,0,0,0.1))",
                                    `drop-shadow(0px 0px 20px ${brand.colors.goalFlash}80)`,
                                    "drop-shadow(0px 1px 2px rgba(0,0,0,0.1))"
                                ]
                            } : { scale: 1, filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.1))" }}
                            transition={{ duration: 1.5, repeat: isGoalFlashing ? Infinity : 0, ease: "easeInOut" }}
                            className="text-[3.25rem] text-score-neon font-extrabold italic tracking-tighter leading-none origin-center inline-block"
                        >
                            {matchScore.team1 ?? 0} - {matchScore.team2 ?? 0}
                        </motion.div>
                    )}
                </div>

                <div className="w-[30%] flex justify-center">
                    <TeamDisplay team={fixture.awayTeam} onClick={onTeamClick ? () => onTeamClick('AWAY') : undefined} />
                </div>
            </div>
        </div>
    );
};
